// src/admin/state/adminStore.js
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import EquityService from '../../services/equityService';

export const useAdminStore = create(
  devtools(
    (set, get) => ({
      // --- STATE ---
      // Companies
      companies: [],
      companiesLoading: false,
      companiesError: null,
      
      // Shareholders
      shareholders: [],
      shareholdersLoading: false,
      shareholdersError: null,
      
      // Share Classes
      shareClasses: [],
      shareClassesLoading: false,
      shareClassesError: null,
      
      // System Configuration
      emailTemplates: [],
      dataroomTemplates: [],
      geographicData: {
        countries: [],
        states: []
      },
      importTemplates: [],
      
      // UI State
      activeTab: 'dashboard',
      selectedCompany: null,
      modals: {
        createCompany: false,
        editCompany: false,
        createShareholder: false,
        editShareholder: false,
        createShareClass: false,
        editShareClass: false
      },
      
      // Messages
      messages: [],

      // --- ACTIONS ---
      
      // Companies
      loadCompanies: async () => {
        set({ companiesLoading: true, companiesError: null });
        try {
          const companies = await EquityService.fetchAllCompanies();
          set({ 
            companies: companies || [], 
            companiesLoading: false 
          });
          return companies || [];
        } catch (error) {
          console.error('Error loading companies:', error);
          set({ 
            companies: [], 
            companiesLoading: false, 
            companiesError: error 
          });
          return [];
        }
      },

      createCompany: async (companyData) => {
        try {
          await EquityService.createCompany(companyData);
          // Refresh companies list
          await get().loadCompanies();
          get().addMessage('Company created successfully', 'success');
          return true;
        } catch (error) {
          console.error('Error creating company:', error);
          get().addMessage('Error creating company', 'error');
          return false;
        }
      },

      updateCompany: async (id, companyData) => {
        try {
          await EquityService.updateCompany(id, companyData);
          // Refresh companies list
          await get().loadCompanies();
          get().addMessage('Company updated successfully', 'success');
          return true;
        } catch (error) {
          console.error('Error updating company:', error);
          get().addMessage('Error updating company', 'error');
          return false;
        }
      },

      deleteCompany: async (id) => {
        try {
          await EquityService.deleteCompany(id);
          // Refresh companies list
          await get().loadCompanies();
          get().addMessage('Company deleted successfully', 'success');
          return true;
        } catch (error) {
          console.error('Error deleting company:', error);
          get().addMessage('Error deleting company', 'error');
          return false;
        }
      },

      // Shareholders
      loadShareholders: async () => {
        set({ shareholdersLoading: true, shareholdersError: null });
        try {
          const shareholders = await EquityService.fetchAllShareholders();
          set({ 
            shareholders: shareholders || [], 
            shareholdersLoading: false 
          });
          return shareholders || [];
        } catch (error) {
          console.error('Error loading shareholders:', error);
          set({ 
            shareholders: [], 
            shareholdersLoading: false, 
            shareholdersError: error 
          });
          return [];
        }
      },

      createShareholder: async (shareholderData) => {
        try {
          await EquityService.createShareholder(shareholderData);
          // Refresh shareholders list
          await get().loadShareholders();
          get().addMessage('Shareholder created successfully', 'success');
          return true;
        } catch (error) {
          console.error('Error creating shareholder:', error);
          get().addMessage('Error creating shareholder', 'error');
          return false;
        }
      },

      updateShareholder: async (id, shareholderData) => {
        try {
          await EquityService.updateShareholder(id, shareholderData);
          // Refresh shareholders list
          await get().loadShareholders();
          get().addMessage('Shareholder updated successfully', 'success');
          return true;
        } catch (error) {
          console.error('Error updating shareholder:', error);
          get().addMessage('Error updating shareholder', 'error');
          return false;
        }
      },

      deleteShareholder: async (id) => {
        try {
          await EquityService.deleteShareholder(id);
          // Refresh shareholders list
          await get().loadShareholders();
          get().addMessage('Shareholder deleted successfully', 'success');
          return true;
        } catch (error) {
          console.error('Error deleting shareholder:', error);
          get().addMessage('Error deleting shareholder', 'error');
          return false;
        }
      },

      // Share Classes
      loadShareClasses: async () => {
        set({ shareClassesLoading: true, shareClassesError: null });
        try {
          const companies = get().companies;
          const allShareClasses = [];
          
          if (companies && companies.length > 0) {
            for (const company of companies) {
              if (company.id) {
                try {
                  const shareClasses = await EquityService.fetchShareClasses(company.id);
                  if (shareClasses && shareClasses.length > 0) {
                    allShareClasses.push(...shareClasses);
                  }
                } catch (error) {
                  console.error(`Error fetching share classes for company ${company.id}:`, error);
                }
              }
            }
          }
          
          set({ 
            shareClasses: allShareClasses, 
            shareClassesLoading: false 
          });
          return allShareClasses;
        } catch (error) {
          console.error('Error loading share classes:', error);
          set({ 
            shareClasses: [], 
            shareClassesLoading: false, 
            shareClassesError: error 
          });
          return [];
        }
      },

      createShareClass: async (shareClassData) => {
        try {
          await EquityService.createShareClass(shareClassData);
          // Refresh share classes list
          await get().loadShareClasses();
          get().addMessage('Share class created successfully', 'success');
          return true;
        } catch (error) {
          console.error('Error creating share class:', error);
          get().addMessage('Error creating share class', 'error');
          return false;
        }
      },

      updateShareClass: async (id, shareClassData) => {
        try {
          await EquityService.updateShareClass(id, shareClassData);
          // Refresh share classes list
          await get().loadShareClasses();
          get().addMessage('Share class updated successfully', 'success');
          return true;
        } catch (error) {
          console.error('Error updating share class:', error);
          get().addMessage('Error updating share class', 'error');
          return false;
        }
      },

      deleteShareClass: async (id) => {
        try {
          await EquityService.deleteShareClass(id);
          // Refresh share classes list
          await get().loadShareClasses();
          get().addMessage('Share class deleted successfully', 'success');
          return true;
        } catch (error) {
          console.error('Error deleting share class:', error);
          get().addMessage('Error deleting share class', 'error');
          return false;
        }
      },

      // UI Actions
      setActiveTab: (tab) => set({ activeTab: tab }),
      
      setSelectedCompany: (company) => set({ selectedCompany: company }),
      
      openModal: (modalName) => 
        set(state => ({
          modals: { ...state.modals, [modalName]: true }
        })),
      
      closeModal: (modalName) => 
        set(state => ({
          modals: { ...state.modals, [modalName]: false }
        })),

      // Messages
      addMessage: (text, type = 'info') => {
        const id = Date.now();
        set(state => ({ 
          messages: [...state.messages, { id, text, type, timestamp: new Date() }] 
        }));
        // Auto-remove message after 5 seconds
        setTimeout(() => {
          set(state => ({ 
            messages: state.messages.filter(msg => msg.id !== id) 
          }));
        }, 5000);
      },

      removeMessage: (id) => 
        set(state => ({ 
          messages: state.messages.filter(msg => msg.id !== id) 
        })),

      clearMessages: () => set({ messages: [] }),

      // Computed values
      getCompanyName: (companyId) => {
        const company = get().companies.find(c => c.id === companyId);
        return company ? company.name : 'Unknown Company';
      },

      getShareClassesForCompany: (companyId) => {
        return get().shareClasses.filter(sc => sc.company_id === companyId);
      },

      // Stats
      getStats: () => {
        const { companies, shareholders, shareClasses } = get();
        return {
          totalCompanies: companies.length,
          activeCompanies: companies.filter(c => c.status === 'active').length,
          companiesWithShareClasses: companies.filter(c => 
            shareClasses.some(sc => sc.company_id === c.id)
          ).length,
          totalShareholders: shareholders.length,
          totalShareClasses: shareClasses.length,
          investorsCount: shareholders.filter(s => s.shareholder_type === 'Investor').length
        };
      },

      // Initialize data
      initializeData: async () => {
        await Promise.all([
          get().loadCompanies(),
          get().loadShareholders()
        ]);
        // Load share classes after companies are loaded
        await get().loadShareClasses();
      }
    }),
    {
      name: 'admin-store', // unique name for devtools
    }
  )
);

